import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Divider,
    Typography,
    Box,
    Button,
    useTheme,
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    TextField,
    InputAdornment,
    Tooltip,
    Paper
} from '@mui/material';
import {
    Movie as MovieIcon,
    ConfirmationNumber as TicketIcon,
    Close as CloseIcon,
    Favorite as FavoriteIcon
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import {
    getAuthToken,
    Loading,
    EnhancedErrorMessage,
    TicketList,
    EventDetails,
    getPublishedEvent,
    purchaseTicketsV2,
    CheckUserPaid
} from '@virtual-cinema/client-lib';
import AudienceAppBar from '../common/app-bar';
import EventBreadcrumbs from '../components/event-breadcrumbs';
import MovieList from './movie-list';

// Initialize Stripe
const stripePromise = loadStripe('pk_live_51PwxFFP4l93B7TU7BSXMTcpG4CjCh9W3o7DnmeUgweujlhQGolqBlo1azUqUkXuFfItcHB9DVBO4PcpWpSWSRQzh00HyibX22Q');

const ViewEvent = () => {
    const { id } = useParams();
    const location = useLocation();
    const [eventData, setEventData] = useState(null);
    const [eventAsset, setEventAsset] = useState(null);
    const [eventMovies, setEventMovies] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [openCheckoutModal, setOpenCheckoutModal] = useState(false);
    const [hasUserPaid, setHasUserPaid] = useState(false);
    const [donationAmount, setDonationAmount] = useState(0);
    const [showDonationInput, setShowDonationInput] = useState(false);

    const theme = useTheme();
    const ticketsRef = useRef(null);
    const accessToken = getAuthToken();
    const navigate = useNavigate();

    // Function to render formatted text with preserved line breaks
    const renderFormattedText = (text) => {
        if (!text) return null;

        // Split the text by newline characters
        const paragraphs = text.split('\n');

        return paragraphs.map((paragraph, index) => (
            <Typography
                key={index}
                variant="body1"
                paragraph={index < paragraphs.length - 1}
                sx={{
                    whiteSpace: 'pre-line',
                    mb: index < paragraphs.length - 1 ? 2 : 0
                }}
            >
                {paragraph}
            </Typography>
        ));
    };

    useEffect(() => {
        // Check if we should scroll to tickets section
        if (location.hash === '#tickets' ||
            (location.state && location.state.scrollToTickets)) {
            // Slight delay to ensure content is loaded
            setTimeout(() => {
                ticketsRef.current?.scrollIntoView({
                    behavior: location.state?.behavior || 'auto',
                    block: 'start'
                });
            }, 100);
        }
    }, [location, eventData]);

    useEffect(() => {
        const fetchEventData = async () => {
            setIsLoading(true);
            try {
                const { data, error } = await getPublishedEvent(id);
                if (error) {
                    throw new Error(error);
                } else {
                    // Set event data directly without merging with default data
                    setEventData(data.event);
                    setEventAsset(data.eventAsset);
                    setEventMovies(data.content || []);

                    // Set tickets and automatically select the first ticket if available
                    const eventTickets = data.tickets || [];
                    setTickets(eventTickets);
                    if (eventTickets.length > 0) {
                        setSelectedTicket(eventTickets[0]);
                    }

                    // Check if donations are allowed for this event
                    // If event.details is a string, parse it
                    let details = data.event.details;
                    if (typeof details === 'string') {
                        try {
                            details = JSON.parse(details);
                        } catch (e) {
                            details = {};
                        }
                    }

                    setShowDonationInput(details?.allowDonations || false);
                }
            } catch (error) {
                setError(`Failed to fetch event data: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEventData();
    }, [id]);

    useEffect(() => {
        const checkUserPaymentStatus = async () => {
            if (accessToken) {
                try {
                    const { data, error } = await CheckUserPaid(id, accessToken);
                    if (error) {
                        throw new Error(error);
                    }
                    else {
                        setHasUserPaid(data);
                    }
                } catch (error) {
                    console.error("Error checking user payment status:", error);
                    setError(`Failed to check payment status: ${error.message}`);
                }
            }
        };

        checkUserPaymentStatus();
    }, [id, accessToken]);

    const handleTicketSelection = (ticket) => {
        setSelectedTicket(selectedTicket && selectedTicket.id === ticket.id ? null : ticket);
    };

    const handleLogin = () => {
        navigate("/login");
    };

    const handleDonationChange = (event) => {
        const value = parseFloat(event.target.value);
        setDonationAmount(isNaN(value) ? 0 : Math.max(0, value));
    };

    const handleBuyTicketsClick = async () => {
        if (!accessToken) {
            handleLogin();
            return;
        }

        if (!selectedTicket) return;

        setIsLoading(true);
        setError(null);

        try {
            const { data, error } = await purchaseTicketsV2(
                selectedTicket.id,
                accessToken,
                donationAmount > 0 ? donationAmount : undefined
            );

            if (error) {
                throw new Error(error);
            }

            setClientSecret(data.clientSecret);
            setOpenCheckoutModal(true);
        } catch (err) {
            setError(`Failed to initiate ticket purchase: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseCheckoutModal = () => {
        setOpenCheckoutModal(false);
        setClientSecret(null);
    };

    const handleCancelPayment = () => {
        handleCloseCheckoutModal();
    };

    const totalCost = selectedTicket ? selectedTicket.price : 0;
    const isBuyButtonDisabled = !selectedTicket || isLoading;

    // Parse event details to access donation description
    const getEventDetails = () => {
        if (!eventData || !eventData.details) return {};

        if (typeof eventData.details === 'string') {
            try {
                return JSON.parse(eventData.details);
            } catch (e) {
                return {};
            }
        }

        return eventData.details;
    };

    const eventDetails = getEventDetails();

    return (
        <Container maxWidth="xl">
            <AudienceAppBar />
            {!isLoading && !error && eventData && (
                <EventBreadcrumbs
                    eventTitle={eventData.name}
                    eventId={id}
                />
            )}
            {error ? (
                <EnhancedErrorMessage message={error} />
            ) : isLoading ? (
                <Loading />
            ) : (
                <Container maxWidth="lg" sx={{ mt: 4 }}>
                    <EventDetails event={eventData} eventAsset={eventAsset} />

                    {/* Event Summary Section */}
                    {eventData?.details?.summary && (
                        <Box sx={{ mb: 3, mt: 3 }}>
                            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                                Event Summary
                            </Typography>
                            {renderFormattedText(
                                typeof eventData.details === 'string' && eventData.details.includes('summary')
                                    ? JSON.parse(eventData.details).summary
                                    : eventData.details.summary
                            )}
                        </Box>
                    )}

                    {/* Event Description Section */}
                    {eventData?.details?.description && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                                Event Description
                            </Typography>
                            {renderFormattedText(
                                typeof eventData.details === 'string' && eventData.details.includes('description')
                                    ? JSON.parse(eventData.details).description
                                    : eventData.details.description
                            )}
                        </Box>
                    )}

                    {/* Tickets Section - Display before movie list for better user experience */}
                    {accessToken && hasUserPaid ? (
                        <Alert severity="success" sx={{ mt: 3, mb: 3 }}>
                            You have already purchased a ticket for this event.
                        </Alert>
                    ) : (
                        tickets.length > 0 && (
                            <Box ref={ticketsRef} sx={{ mb: 3 }}>
                                <Divider sx={{ my: 3, bgcolor: 'divider' }} />

                                {/* Available Tickets */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TicketIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="h6" color="primary.main">
                                        Available Tickets
                                    </Typography>
                                </Box>

                                {/* Standard TicketList */}
                                <TicketList
                                    tickets={tickets}
                                    selectedTicket={selectedTicket}
                                    onTicketSelect={handleTicketSelection}
                                />

                                {/* Donation Section */}
                                {showDonationInput && (
                                    <Box sx={{ mt: 3, mb: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 1
                                            }}
                                        >
                                            <FavoriteIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                                            <Typography color="primary.main" variant="subtitle1">
                                                Support this event with a donation
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 2,
                                                border: '1px dashed',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                bgcolor: 'background.paper'
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Add an optional donation to your ticket purchase. {eventDetails.donationDescription ||
                                                    "Your contribution helps support the creators and artists."}
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <TextField
                                                    label="Donation Amount"
                                                    value={donationAmount || ''}
                                                    onChange={handleDonationChange}
                                                    type="number"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ width: '150px' }}
                                                />

                                                {donationAmount > 0 && (
                                                    <Typography variant="body2" color="success.main" sx={{ ml: 2 }}>
                                                        Thank you for your generosity!
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                {/* Total and Checkout Button */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                    <Typography variant="h6">
                                        Total: ${(totalCost + (donationAmount || 0)).toFixed(2)}
                                        {donationAmount > 0 && (
                                            <Tooltip title={`Ticket: $${totalCost.toFixed(2)} + Donation: $${donationAmount.toFixed(2)}`}>
                                                <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                                                    (includes ${donationAmount.toFixed(2)} donation)
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Typography>
                                    {accessToken ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleBuyTicketsClick}
                                            disabled={isBuyButtonDisabled}
                                            sx={{
                                                borderRadius: 20,
                                                px: 3,
                                                py: 1,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                }
                                            }}
                                        >
                                            {isLoading ? 'Processing...' : 'Buy Tickets'}
                                        </Button>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={handleLogin}
                                                sx={{
                                                    borderRadius: 20,
                                                    px: 3,
                                                    py: 1,
                                                    textTransform: 'none',
                                                    mb: 1,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.primary.dark,
                                                    }
                                                }}
                                            >
                                                Login to Buy Tickets
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )
                    )}

                    {/* Movie List Section - Moved after summary, description, and tickets */}
                    {eventMovies && eventMovies.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Divider sx={{ my: 3, bgcolor: 'divider' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <MovieIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" color="primary.main">
                                    Event Lineup
                                </Typography>
                            </Box>
                            <MovieList
                                movies={eventMovies}
                                eventId={id}
                                eventData={eventData} // Pass the eventData to respect content order
                            />
                        </Box>
                    )}
                </Container>
            )}

            <Dialog
                open={openCheckoutModal}
                onClose={handleCloseCheckoutModal}
                maxWidth="md"
                fullWidth
            >
                <DialogActions>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleCloseCheckoutModal}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogActions>
                <DialogContent>
                    {clientSecret && (
                        <EmbeddedCheckoutProvider
                            stripe={stripePromise}
                            options={{ clientSecret }}
                        >
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelPayment} color="primary">
                        Cancel Payment
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ViewEvent;